/**
 * apiClient.ts
 * Central Axios-like fetch wrapper for all backend API calls.
 * All API routes are prefixed with /api (proxied to port 8000 via package.json "proxy").
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api';

let inMemoryToken: string | null = null;
let refreshSubscribers: ((token: string) => void)[] = [];
let isRefreshing = false;
let onAuthFailureCallback: (() => void) | null = null;

export const setAccessToken = (token: string | null) => {
  inMemoryToken = token;
};

export const getAccessToken = () => {
  return inMemoryToken;
};

export const registerAuthFailureCallback = (cb: () => void) => {
  onAuthFailureCallback = cb;
};

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
}

async function performRefresh(): Promise<string> {
  if (isRefreshing) {
    return new Promise(resolve => {
      subscribeTokenRefresh(token => resolve(token));
    });
  }

  isRefreshing = true;

  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });

    if (!res.ok) {
      throw new Error('Refresh failed');
    }

    const data = await res.json();
    const token = data.accessToken;
    setAccessToken(token);
    onRefreshed(token);
    isRefreshing = false;
    return token;
  } catch (err) {
    isRefreshing = false;
    throw err;
  }
}

async function request<T>(method: string, path: string, body?: unknown, retry = true): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (inMemoryToken) headers['Authorization'] = `Bearer ${inMemoryToken}`;

  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });

    if (!res.ok) {
      if (res.status === 401 && retry && path !== '/auth/login' && path !== '/auth/refresh') {
        try {
          const newToken = await performRefresh();
          headers['Authorization'] = `Bearer ${newToken}`;
          const retryRes = await fetch(`${API_BASE}${path}`, {
            method,
            headers,
            body: body !== undefined ? JSON.stringify(body) : undefined,
            credentials: 'include',
          });
          if (!retryRes.ok) {
            const error = await retryRes.json().catch(() => ({ error: retryRes.statusText }));
            throw new Error(error?.error || retryRes.statusText);
          }
          return retryRes.json();
        } catch (refreshErr) {
          if (onAuthFailureCallback) {
            onAuthFailureCallback();
          }
          throw refreshErr;
        }
      }

      const error = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(error?.error || res.statusText);
    }

    return res.json();
  } catch (error) {
    throw error;
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};


// ───────────────── Products ─────────────────

export interface BackendProduct {
  id: string;
  sku: string;
  name: string;
  slug: string;
  brand: string;
  description: string | null;
  specifications: string | null;
  features: string | null;
  price: string;
  oldPrice: string | null;
  discount: string | null;
  rating: string;
  reviewsCount: number;
  inStock: boolean;
  stockQuantity: number;
  isDeal: boolean;
  tags: string[];
  categoryId: string;
  subcategoryId: string | null;
  image: string;
  images: { id: string; url: string }[];
  createdAt: string;
  updatedAt: string;
}

export interface ProductsResponse {
  products: BackendProduct[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ProductFilters {
  search?: string;
  category?: string;
  subcategory?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export const productsApi = {
  getAll: (filters: ProductFilters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== '') params.append(k, String(v));
    });
    const qs = params.toString();
    return api.get<ProductsResponse>(`/products${qs ? '?' + qs : ''}`);
  },
  getById: (id: string) => api.get<BackendProduct>(`/products/${id}`),
  create: (data: Partial<BackendProduct>) => api.post<BackendProduct>('/products', data),
  update: (id: string, data: Partial<BackendProduct>) => api.put<BackendProduct>(`/products/${id}`, data),
  delete: (id: string) => api.delete<{ message: string }>(`/products/${id}`),
  bulkImport: (products: unknown[], conflictMode: string, username: string, filename: string) =>
    api.post<{ added: number; updated: number; skipped: number; failed: number }>(
      '/products/import',
      { products, conflictMode, username, filename }
    ),
  bulkStockUpdate: (products: unknown[], username: string, filename: string) =>
    api.post<{ updated: number; failed: number }>(
      '/products/import-stock',
      { products, username, filename }
    ),
};

// ───────────────── Categories ─────────────────

export interface BackendCategory {
  id: string;
  name: string;
  slug: string;
  path: string;
  icon: string;
  description: string;
  children: BackendCategory[];
}

export const categoriesApi = {
  getAll: () => api.get<BackendCategory[]>('/categories'),
  create: (data: { name: string; description?: string; parentId?: string }) =>
    api.post<BackendCategory>('/categories', data),
  update: (id: string, data: Partial<BackendCategory>) =>
    api.put<BackendCategory>(`/categories/${id}`, data),
  delete: (id: string) => api.delete<{ message: string }>(`/categories/${id}`),
};

// ───────────────── Orders ─────────────────

export const ordersApi = {
  getAll: (userId?: string) => {
    const qs = userId ? `?userId=${userId}` : '';
    return api.get<unknown[]>(`/orders${qs}`);
  },
  create: (data: unknown) => api.post<unknown>('/orders', data),
};

// ───────────────── RFQs ─────────────────

export const rfqsApi = {
  getAll: (userId?: string) => {
    const qs = userId ? `?userId=${userId}` : '';
    return api.get<unknown[]>(`/rfqs${qs}`);
  },
  create: (data: unknown) => api.post<unknown>('/rfqs', data),
};

// ───────────────── Auth ─────────────────

export interface LoginResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    name: string | null;
    role: string;
  };
}

export const authApi = {
  login: (email: string, password: string) =>
    api.post<LoginResponse>('/auth/login', { email, password }),
  register: (data: { email: string; password: string; name: string }) =>
    api.post<LoginResponse['user']>('/auth/register', data),
  getUsers: () => api.get<LoginResponse['user'][]>('/auth/users'),
  refresh: () => api.post<LoginResponse>('/auth/refresh', {}),
  logout: () => api.post<{ message: string }>('/auth/logout', {}),
};


// ───────────────── Settings ─────────────────

export const settingsApi = {
  get: (key: string) => api.get<{ key: string; value: string }>(`/settings/${key}`),
  set: (key: string, value: string) =>
    api.post<{ key: string; value: string }>(`/settings/${key}`, { value }),
};

// ───────────────── Import Logs ─────────────────

export const logsApi = {
  getImportLogs: () => api.get<unknown[]>('/logs/imports'),
};
